import { type Project, useStores } from '@littlebird/sdk';
import { EMPTY_PROJECT_CAPABILITIES } from '@littlebird/ui-utils';
import { observer } from 'mobx-react-lite';
import { Fragment, type MouseEvent, useState } from 'react';

import { Icons, type LBIcon } from '@/assets/icons/icons';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from '@/components/ui/context-menu';
import { DropdownMenu } from '@/components/ui/dropdown-menu';
import { EntityIcon } from '@/components/ui/icon-picker';
import { RenameField } from '@/components/ui/RenameField/RenameField';
import { useRenameField } from '@/components/ui/RenameField/useRenameField';
import {
  ROUND_ICON_BUTTON_EDGE_INSET,
  RoundIconButton,
} from '@/design-system-v2/round-icon-button';
import { SidebarRow } from '@/design-system-v2/sidebar-row';
import { useTabs } from '@/features/tabs/tabs';
import { cn } from '@/lib/utils';

import { useProjectActions } from './project-actions';
import { getProjectSidebarIcon } from './project-sidebar-icon';
import { ShareProjectModal } from './share-project-modal';
import { useChatProjectDropTarget } from './use-chat-project-drop';

const dropdownItemClassName =
  'gap-2 rounded-lg px-2 py-1.5 smoothing-default hover:bg-surface-hover data-[highlighted]:bg-surface-hover [&>svg]:opacity-75';

interface ProjectMenuItem {
  id: string;
  label: string;
  icon: LBIcon;
  onClick: (event: MouseEvent) => void;
  disabled?: boolean;
  danger?: boolean;
  separator?: boolean;
}

export const ProjectSidebarRow = observer(function ProjectSidebarRow({
  project,
  isActive,
  onDelete,
  onLeave,
  onSelect,
  onFavorite,
  isExpanded,
  onToggleExpanded,
}: {
  project: Project;
  isActive: boolean;
  onDelete: () => void;
  onLeave: () => void;
  onSelect: (e?: MouseEvent) => void;
  onFavorite: () => void;
  isExpanded?: boolean;
  onToggleExpanded?: () => void;
}) {
  const { projects } = useStores();
  const { renameProject } = useProjectActions();
  const { isDragOver, dropTargetProps } = useChatProjectDropTarget(project.id);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmLeave, setConfirmLeave] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const { openTab } = useTabs();
  const { renameFieldRef, showRenameInputField } = useRenameField();

  const { canRename, canDelete, canLeave } =
    project?.capabilities ?? EMPTY_PROJECT_CAPABILITIES;
  const sidebarIcon = getProjectSidebarIcon(project, isExpanded);

  const itemPaths = () => [
    ...projects.getThreads(project.id).map((thread) => `/chats/${thread.id}`),
    ...projects
      .getMeetings(project.id)
      .map((meeting) => `/meetings/${meeting.id}`),
  ];
  const hasItems =
    projects.getThreads(project.id).length > 0 ||
    projects.getMeetings(project.id).length > 0;
  const openAllInTabs = () => {
    itemPaths().forEach((path) => openTab({ path }, { reuseExisting: true }));
  };
  const handleLeave = (event: MouseEvent) => {
    setConfirmLeave((previousValue) => {
      if (!previousValue) {
        event.preventDefault();
        event.stopPropagation();
        return true;
      }

      onLeave();
      return false;
    });
  };
  const handleDelete = (event: MouseEvent) => {
    setConfirmDelete((previousValue) => {
      if (!previousValue) {
        event.preventDefault();
        event.stopPropagation();
        return true;
      }

      onDelete();
      return false;
    });
  };
  const menuItems: ProjectMenuItem[] = [
    {
      id: 'favorite',
      label: project.starred ? 'Unfavorite' : 'Favorite',
      icon: project.starred ? Icons.starFilled : Icons.star,
      onClick: onFavorite,
    },
    {
      id: 'edit',
      label: 'Edit',
      icon: Icons.squarePen,
      onClick: onSelect,
    },
    {
      id: 'open-all',
      label: 'Open all in tabs',
      icon: Icons.columns,
      onClick: openAllInTabs,
      disabled: !hasItems,
    },
    ...(canRename
      ? [
          {
            id: 'rename',
            label: 'Rename',
            icon: Icons.edit,
            onClick: () => setTimeout(showRenameInputField, 0),
          },
        ]
      : []),
    {
      id: 'share',
      label: 'Share',
      icon: Icons.share,
      onClick: () => setShareOpen(true),
    },
    ...(canLeave
      ? [
          {
            id: 'leave',
            label: confirmLeave ? 'Leave project?' : 'Leave project',
            icon: confirmLeave ? Icons.check : Icons.logOut,
            onClick: handleLeave,
            danger: true,
            separator: true,
          },
        ]
      : []),
    ...(canDelete
      ? [
          {
            id: 'delete',
            label: confirmDelete ? 'Confirm?' : 'Delete',
            icon: confirmDelete ? Icons.check : Icons.trash,
            onClick: handleDelete,
            danger: true,
            separator: true,
          },
        ]
      : []),
  ];
  const renderMenuItems = (variant: 'context' | 'dropdown') =>
    menuItems.map((item) => {
      const Icon = item.icon;

      return (
        <Fragment key={item.id}>
          {item.separator &&
            (variant === 'context' ? (
              <ContextMenuSeparator />
            ) : (
              <DropdownMenu.Separator className="mx-0 ml-[5%] w-[90%]" />
            ))}
          {variant === 'context' ? (
            <ContextMenuItem
              icon={Icon}
              disabled={item.disabled}
              className={item.danger ? 'text-danger-9' : undefined}
              onClick={item.onClick}
            >
              {item.label}
            </ContextMenuItem>
          ) : (
            <DropdownMenu.Item
              disabled={item.disabled}
              className={cn(
                dropdownItemClassName,
                item.danger && 'text-danger-9'
              )}
              onClick={item.onClick}
            >
              <Icon />
              {item.label}
            </DropdownMenu.Item>
          )}
        </Fragment>
      );
    });

  return (
    <ContextMenu
      onOpenChange={(open) => {
        if (!open) {
          setConfirmDelete(false);
          setConfirmLeave(false);
        }
      }}
    >
      <ContextMenuTrigger asChild>
        <SidebarRow.Root
          selected={isActive || isDragOver}
          className="group/project-row"
          onClick={onSelect}
          onKeyDown={(event) => {
            if (event.target !== event.currentTarget) return;
            if (event.key !== 'Enter' && event.key !== ' ') return;
            event.preventDefault();
            onSelect();
          }}
          role="button"
          tabIndex={0}
          data-chat-drop-over={isDragOver ? '' : undefined}
          {...dropTargetProps}
        >
          <SidebarRow.Leading className="group/project-leading">
            <span
              aria-hidden="true"
              className={cn(
                'absolute inset-0 inline-flex items-center justify-center transition-opacity duration-150',
                onToggleExpanded &&
                  'touch:opacity-0 group-hover/project-row:opacity-0 group-has-[:focus-visible]/project-leading:opacity-0'
              )}
            >
              <SidebarRow.Icon>
                <EntityIcon icon={sidebarIcon} size="100%" />
              </SidebarRow.Icon>
            </span>
            {onToggleExpanded && (
              <button
                type="button"
                aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${project.name}`}
                aria-expanded={isExpanded}
                className={cn(
                  'absolute inset-0 inline-flex items-center justify-center rounded-sm opacity-0',
                  "after:absolute after:-inset-y-1.5 after:-left-2 after:-right-1.5 after:content-['']",
                  'transition-[color,background-color,opacity] duration-150 hover:bg-secondary-active hover:text-foreground focus-visible:bg-secondary-active focus-ring',
                  'touch:opacity-100',
                  'group-hover/project-row:opacity-100 group-has-[:focus-visible]/project-leading:opacity-100'
                )}
                onClick={(event) => {
                  event.stopPropagation();
                  onToggleExpanded();
                }}
              >
                <Icons.chevronDown
                  aria-hidden="true"
                  className={cn(
                    'size-3.5 shrink-0 transition-transform duration-200 ease-out',
                    !isExpanded && '-rotate-90'
                  )}
                />
              </button>
            )}
          </SidebarRow.Leading>
          <RenameField
            ref={renameFieldRef}
            value={project.name}
            onRename={(name) => {
              void renameProject(project.id, name);
            }}
            className="truncate"
            disabled={!canRename}
          />
          {onToggleExpanded && (
            <DropdownMenu.Root
              onOpenChange={(open) => {
                if (!open) {
                  setConfirmDelete(false);
                  setConfirmLeave(false);
                }
              }}
            >
              <DropdownMenu.Trigger asChild>
                <RoundIconButton
                  icon={Icons.moreHorizontal}
                  size="xs"
                  label={`More actions for ${project.name}`}
                  onMouseDown={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                  }}
                  onClick={(event) => event.stopPropagation()}
                  className={cn(
                    '-my-0.5 ml-auto hidden touch:flex group-hover/project-row:flex',
                    ROUND_ICON_BUTTON_EDGE_INSET.xs,
                    'group-has-[:focus-visible]/project-row:flex',
                    'data-[state=open]:flex'
                  )}
                />
              </DropdownMenu.Trigger>
              <DropdownMenu.Content
                align="end"
                fit
                className="min-w-[8rem] rounded-lg p-2 shadow-md"
                onMouseDown={(event) => event.stopPropagation()}
                onClick={(event) => event.stopPropagation()}
              >
                {renderMenuItems('dropdown')}
              </DropdownMenu.Content>
            </DropdownMenu.Root>
          )}
        </SidebarRow.Root>
      </ContextMenuTrigger>
      <ContextMenuContent>{renderMenuItems('context')}</ContextMenuContent>
      <ShareProjectModal
        project={project}
        open={shareOpen}
        onOpenChange={setShareOpen}
      />
    </ContextMenu>
  );
});
